<template>
  <div>
    <v-form
      :disabled="form.processing"
      @submit.prevent="submit"
    >
      <v-row>
        <v-col
          cols="12"
          sm="6"
        >
          <v-switch
            v-model="form.auto_sort_cats"
            class="mt-0"
            dense
            color="primary"
            :label="$t('Automatically sort categories')"
            :hint="$t('This setting will automatically sort categories and subcategories by popularity')"
            persistent-hint
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-switch
            v-model="form.auto_sort_ques"
            class="mt-0"
            dense
            color="primary"
            :label="$t('Automatically sort questions')"
            :hint="$t('This setting will automatically sort questions by popularity')"
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          sm="6"
        >
          <v-switch
            v-model="form.subcats_bottom"
            class="mt-0"
            dense
            color="primary"
            :label="$t('Subcategories at bottom')"
            :hint="$t('Show subcategories at the bottom of each category instead of the top')"
            persistent-hint
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-switch
            v-model="form.user_feedback"
            class="mt-0"
            dense
            color="primary"
            :label="$t('User feedback')"
            :hint="$t('Enabling user feedback will allow users to up or downvote the answer of your questions')"
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          sm="6"
        >
          <v-switch
            v-model="form.multiple_open"
            class="mt-0"
            dense
            color="primary"
            :label="$t('Multiple questions open')"
            :hint="$t('This setting makes it possible to have multiple questions open')"
            persistent-hint
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <!-- <v-switch
            v-model="form.branding"
            class="mt-0"
            dense
            color="primary"
            :true-value="false"
            :false-value="true"
            :label="$t('Disable webcomet branding')"
            :hint="$t('Removes the \'powered by webcomet\' (will still be shown if viewed via the webcomet hosted url)')"
            persistent-hint
          /> -->
        </v-col>
      </v-row>

      <v-row v-if="faq.available_languages.length > 1">
        <v-col
          cols="12"
          sm="6"
        >
          <v-switch
            v-model="form.language_switch"
            class="mt-0"
            dense
            color="primary"
            :label="$t('Show language switch')"
            :hint="$t('This makes it possible for the users to change language themselves')"
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-row class="mb-6">
        <v-col
          cols="12"
          sm="6"
        >
          <div class="mb-3">
            <p class="mb-0 subtitle-2">
              {{ $t('Max visible questions per category') }}
            </p>
          </div>

          <v-text-field
            v-model.number="form.visible_ques_cat"
            style="width: 120px"
            :placeholder="$t('All')"
            dense
            outlined
            append-outer-icon="mdi-plus"
            hide-details
            prepend-icon="mdi-minus"
            @click:append-outer="increment('visible_ques_cat')"
            @click:prepend="decrement('visible_ques_cat')"
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <div class="mb-3">
            <p class="mb-0 subtitle-2">
              {{ $t('Max visible questions per subcategory') }}
            </p>
          </div>

          <v-text-field
            v-model.number="form.visible_ques_subcat"
            style="width: 120px"
            :placeholder="$t('All')"
            dense
            hide-details
            outlined
            append-outer-icon="mdi-plus"
            prepend-icon="mdi-minus"
            @click:append-outer="increment('visible_ques_subcat')"
            @click:prepend="decrement('visible_ques_subcat')"
          />
        </v-col>
      </v-row>

      <v-expansion-panels>
        <v-expansion-panel>
          <v-expansion-panel-header>{{ $t('Labels and texts') }}</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <div class="mb-3">
                  <p class="mb-0 subtitle-2">
                    {{ $t('Show all button text') }}
                  </p>
                </div>

                <v-text-field
                  v-model="form.show_all"
                  :placeholder="defaultStrings.embed_show_all"
                  dense
                  class="mb-3"
                  hide-details
                  outlined
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>{{ $t('Styling options') }}</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <color-picker
                  v-model="form.ques_bg_color"
                  default-value="#FAFAFA"
                  :label="$t('Question background color')"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <color-picker
                  v-model="form.open_ques_bg_color"
                  default-value="#F5F5F5"
                  :label="$t('Opened question background color')"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <color-picker
                  v-model="form.ques_title_font_color"
                  :default-value="$t('Inherit')"
                  :label="$t('Question title font color')"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <color-picker
                  v-model="form.open_ques_title_font_color"
                  :default-value="$t('Inherit')"
                  :label="$t('Opened question title font color')"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <color-picker
                  v-model="form.link_color"
                  :default-value="$t('Inherit')"
                  :label="$t('Link color')"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <color-picker
                  v-model="form.cat_icon_bg_color"
                  :default-value="$t('None')"
                  :label="$t('Category icon background color')"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <color-picker
                  v-model="form.answer_font_color"
                  :default-value="$t('Inherit')"
                  :label="$t('Answer font color')"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <v-switch
                  v-model="form.shadows"
                  :label="$t('No shadows')"
                  :true-value="0"
                  :false-value="1"
                />

                <v-switch
                  v-model="form.rounded_corners"
                  :label="$t('Rounded corners')"
                  :true-value="1"
                  :false-value="0"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>{{ $t('Custom CSS') }}</v-expansion-panel-header>
          <v-expansion-panel-content>
            <code-editor
              v-model="form.css"
              language="css"
              style="min-height: 450px"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <portal to="faq-settings-layout-submit">
        <v-btn
          type="submit"
          color="primary"
          :loading="form.processing"
          :disabled="form.processing"
          @click="submit"
        >
          {{ $t('Save changes') }}
        </v-btn>
      </portal>
    </v-form>
  </div>
</template>

<script>
import Layout from '@/components/shared/TheLayout';
import TabsLayout from '@/components/faq/TabsLayout';
import SettingsLayout from '@/components/faq/settings/TabsLayout';
import unsavedFormWarningMixin from '@/mixins/faq/unsaved-form-warning';

export default {
  mixins: [ unsavedFormWarningMixin ],

  layout: (h, page) => h(Layout, {
    props: {
      noWrappingContainer: true,
      pageTitle: page.data.props.faq.default_translation.name
    }
  }, [h(TabsLayout, {
    props: {
      activeTab: 'settings',
      faq: page.data.props.faq
    },
    ref: 'mainLayout'
  }, [h(SettingsLayout, {
      props: {
        faq: page.data.props.faq,
        activeTab: 'embed',
        hideComponentSwitch: true
      },
      ref: 'settingsLayout'
    }, [page])
  ])]),

  props: {
    faq: {
      type: Object,
      required: true
    },
    defaultStrings: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      form: this.$inertia.form({
        auto_sort_cats: this.faq.embed_auto_sort_cats,
        auto_sort_ques: this.faq.embed_auto_sort_ques,
        subcats_bottom: this.faq.embed_subcats_bottom,
        visible_ques_cat: this.faq.embed_visible_ques_cat,
        visible_ques_subcat: this.faq.embed_visible_ques_subcat,
        user_feedback: this.faq.embed_user_feedback,
        css: this.faq.embed_css,
        branding: this.faq.embed_branding,
        language_switch: this.faq.embed_language_switch,
        multiple_open: this.faq.embed_multiple_open,
        show_all: this.faq.default_translation.embed_show_all,
        ques_bg_color: this.faq.embed_ques_bg_color,
        open_ques_bg_color: this.faq.embed_open_ques_bg_color,
        ques_title_font_color: this.faq.embed_ques_title_font_color,
        open_ques_title_font_color: this.faq.embed_open_ques_title_font_color,
        cat_icon_bg_color: this.faq.embed_cat_icon_bg_color,
        link_color: this.faq.embed_link_color,
        answer_font_color: this.faq.embed_answer_font_color,
        shadows: this.faq.embed_shadows,
        rounded_corners: this.faq.embed_rounded_corners,
      }),
      previewKey: 0,
    }
  },

  created() {
    this.$root.preview.faq.component = 'embed';

    window.onbeforeunload = () => this.form.isDirty ? '' : null;
  },

  methods: {
    submit() {
      const url = this.route('faq-settings.embed.update', this.faq.id);
      this.form.patch(url.toString(), {
        onSuccess: () => {
          this.$root.refreshFaqPreview();
        }
      });
    },

    increment(propName) {
      if (this.form[propName] === null) {
        this.form[propName] = 1;
      } else {
        this.form[propName]++;
      }
    },

    decrement(propName) {
      if (this.form[propName] === null) {
        return;
      }
      if (this.form[propName] === 1) {
        this.form[propName] = null;
      } else {
        this.form[propName]--;
      }
    },
  }
}
</script>